import type { ResponsiveObject } from '@chakra-ui/styled-system';
import type { breakpoints } from '@/theme';
import { ExperienceLayouts } from '@/types/gql.generated';
import { PORTRAIT_WIDTH } from '../printables/css/printableStyles';
import type { HSL } from './types';

export type ResponsiveValue<T> = T | ResponsiveObject<T>;

/**
 * Chakra doesn't allow setting a CSS variable that contains a comma.
 * For example, this:
 *  --shadow: 0 0 0 rgba(0, 0, 0, 0.25), 2px 2px 2px rgba(0, 0, 0, 0.25);
 *
 * Results in:
 *  --shadow: 0 0 0 rgba(0, 0, 0 0.25);
 *
 * To work around this limitation, we set the shadow values separately
 * and use an empty shadow variable in the second position in cases where
 * we only want one shadow.
 */
export const NO_SHADOW = '0 0 0 rgba(0, 0, 0)';

export const MIN_ALPHA = 8;
export const MAX_ALPHA = 88;

export const ALPHA_THRESHOLD = 0.4;

export const DEFAULT_PAGE_WIDTH = 'default';

export const BoldnessSteps = {
  XS: 0,
  SM: 25,
  MD: 50,
  LG: 75,
  XL: 100,
} as const;

export type BoldnessStepValue =
  (typeof BoldnessSteps)[keyof typeof BoldnessSteps];

export const SparkleSteps = {
  XS: 0,
  SM: 20,
  MD: 40,
  LG: 60,
  XL: 80,
  XXL: 100,
} as const;

export type SparkleStepValue = (typeof SparkleSteps)[keyof typeof SparkleSteps];

export const boldnessStepConfigs = {
  [BoldnessSteps.XS]: ({ isMobileBreakpoint }) => ({
    card: {
      border: 'none',
      padding: isMobileBreakpoint ? '20px' : '28px',
      shadow1: NO_SHADOW,
      shadow2: NO_SHADOW,
    },
  }),
  [BoldnessSteps.SM]: ({ isMobileBreakpoint }) => ({
    card: {
      border: 'none',
      padding: isMobileBreakpoint ? '20px' : '28px',
      shadow1: '0px 0px 14px 0px rgba(0, 0, 0, 0.25)',
      shadow2: NO_SHADOW,
    },
  }),
  [BoldnessSteps.MD]: ({ isMobileBreakpoint }) => ({
    card: {
      border: '2px solid #000',
      padding: isMobileBreakpoint ? '20px' : '28px',
      shadow1: '0px 0px 14px 0px rgba(0, 0, 0, 0.25)',
      shadow2: NO_SHADOW,
    },
  }),
  [BoldnessSteps.LG]: ({ isMobileBreakpoint }) => ({
    card: {
      border: '2px solid #000',
      padding: isMobileBreakpoint ? '20px' : '28px',
      shadow1: '7px 7px 0px 0px #000',
      shadow2: '0px 0px 14px 0px rgba(0, 0, 0, 0.25)',
    },
  }),
  [BoldnessSteps.XL]: ({ isMobileBreakpoint }) => ({
    card: {
      border: '3px solid #000',
      padding: isMobileBreakpoint ? '20px' : '28px',
      shadow1: '7px 7px 0px 0px #000',
      shadow2: '0px 0px 14px 0px rgba(0, 0, 0, 0.25)',
    },
  }),
} satisfies Record<
  BoldnessStepValue,
  (props: { isMobileBreakpoint: boolean }) => Record<string, object>
>;

export const sparkleStepConfigs = {
  [SparkleSteps.XS]: () => ({
    emoji1: {
      position: 'static',
      left: 0,
      fontSize: '28px',
      textShadow: 'none',
    },
    emoji2: {
      display: 'none',
    },
    stars: {
      totalCount: 0,
      clusterCount: 0,
    },
  }),
  [SparkleSteps.SM]: () => ({
    emoji1: {
      position: 'static',
      left: 0,
      fontSize: '28px',
      textShadow: 'none',
    },
    emoji2: {
      display: 'none',
    },
    stars: {
      totalCount: 15,
      clusterCount: 0,
    },
  }),
  [SparkleSteps.MD]: () => ({
    emoji1: {
      position: 'static',
      left: 0,
      fontSize: '28px',
      textShadow: 'none',
    },
    emoji2: {
      display: 'none',
    },
    stars: {
      totalCount: 50,
      clusterCount: 0,
    },
  }),
  [SparkleSteps.LG]: ({ isMobileBreakpoint }) => ({
    emoji1: {
      position: 'absolute',
      left: isMobileBreakpoint ? '-14px' : '-8px',
      fontSize: '36px',
      textShadow: '2px 2px 6px rgba(0, 0, 0, 0.25)',
    },
    emoji2: {
      display: 'none',
    },
    stars: {
      totalCount: 100,
      clusterCount: 0,
    },
  }),
  [SparkleSteps.XL]: ({ isMobileBreakpoint }) => ({
    emoji1: {
      position: 'absolute',
      left: '-14px',
      fontSize: isMobileBreakpoint ? '36px' : '46px',
      textShadow: '2px 2px 6px rgba(0, 0, 0, 0.25)',
    },
    emoji2: {
      display: 'none',
    },
    stars: {
      totalCount: 150,
      clusterCount: 10,
    },
  }),
  [SparkleSteps.XXL]: ({ isMobileBreakpoint }) => ({
    emoji1: {
      position: 'absolute',
      left: '-14px',
      fontSize: isMobileBreakpoint ? '36px' : '46px',
      textShadow: '2px 2px 6px rgba(0, 0, 0, 0.25)',
    },
    emoji2: {
      display: 'block',
    },
    stars: {
      totalCount: 300,
      clusterCount: 10,
    },
  }),
} satisfies Record<
  SparkleStepValue,
  (props: { isMobileBreakpoint: boolean }) => Record<string, object>
>;

export type LayoutConfig = {
  pageWidth: string;
  cardWidth: string;
  blockSpacing: string;
  pageSpacing: string;
  itemSpacing: string;
  pagePadding: ResponsiveValue<string>;
  bgPadding: ResponsiveValue<string>;
  sideContentWidth: ResponsiveValue<string>;
  /** Breakpoint at which side-by-side layouts switch from stacked to horizontal */
  stackingBreakpoint: keyof typeof breakpoints;
};

export const defaultLayoutConfig: LayoutConfig = {
  pageWidth: '868px',
  cardWidth: '868px',
  blockSpacing: 'var(--chakra-space-5)',
  pageSpacing: 'var(--chakra-space-5)',
  itemSpacing: 'var(--chakra-space-1)',
  pagePadding: { base: 'var(--chakra-space-4)', md: 'var(--chakra-space-10)' },
  bgPadding: { base: 'var(--chakra-space-4)', md: 'var(--chakra-space-5)' },
  sideContentWidth: { base: '100%', md: '400px', lg: '500px' },
  stackingBreakpoint: 'md',
} as const;

export const layoutConfigs: Record<
  ExperienceLayouts,
  () => Partial<LayoutConfig>
> = {
  [ExperienceLayouts.ListVertical]: () => ({
    pageWidth: '700px',
    cardWidth: '700px',
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-5)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.ListVerticalWithImages]: () => ({
    pageWidth: '868px',
    cardWidth: '868px',
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-5)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.ListVerticalBulleted]: () => ({
    pageWidth: '868px',
    cardWidth: '868px',
    blockSpacing: 'var(--chakra-space-5)',
    // Separates month sections; rows within a section have no gap and rely on
    // their bottom borders (see usePageStylesForEventList groupStyles).
    pageSpacing: 'var(--chakra-space-6)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.ListVerticalBulletedCondensed]: () => ({}),
  [ExperienceLayouts.SideHeader]: () => ({
    pageWidth: '1200px',
    cardWidth: '700px',
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-5)',
    itemSpacing: 'var(--chakra-space-1)',
    sideContentWidth: { base: '100%', md: '400px', lg: '500px' },
  }),
  [ExperienceLayouts.SideHeaderV2]: () => ({
    pageWidth: '1400px',
    cardWidth: '650px',
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-5)',
    itemSpacing: 'var(--chakra-space-1)',
    sideContentWidth: { base: '100%', md: '350px', lg: '500px', xl: '650px' },
  }),
  [ExperienceLayouts.Grid]: () => ({
    pageWidth: '1312px',
    cardWidth: '1312px',
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-1)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.ListTable]: () => ({
    pageWidth: '1312px',
    cardWidth: '1312px',
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-1)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.SingleImage]: () => ({
    pageWidth: '868px',
    cardWidth: '868px',
    pageSpacing: 'var(--chakra-space-2)',
    blockSpacing: 'var(--chakra-space-5)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.Week]: () => ({}),
  [ExperienceLayouts.Planner]: () => ({
    pageWidth: '100%',
    pagePadding: '0px',
  }),
  [ExperienceLayouts.PrintVertical]: () => ({
    cardWidth: `${PORTRAIT_WIDTH}px`,
    blockSpacing: 'var(--chakra-space-5)',
    pageSpacing: 'var(--chakra-space-1)',
    itemSpacing: 'var(--chakra-space-1)',
  }),
  [ExperienceLayouts.PrintableWeek]: () => ({}),
  [ExperienceLayouts.PrintableYearly3BoxMonthCard]: () => ({}),
  [ExperienceLayouts.PrintableYearly6BoxMonthCard]: () => ({}),
  [ExperienceLayouts.PrintableYearlyMonthCard]: () => ({}),
  [ExperienceLayouts.PrintableListVertical]: () => ({}),
  [ExperienceLayouts.PrintableList]: () => ({}),
  [ExperienceLayouts.PrintableCustom]: () => ({}),
  [ExperienceLayouts.PrintableCalendar]: () => ({}),

  [ExperienceLayouts.PlannerCalendar]: () => ({}),
  [ExperienceLayouts.PlannerMonth]: () => ({}),
  [ExperienceLayouts.PlannerWeek]: () => ({}),
  [ExperienceLayouts.DayByCategory]: () => ({}),
};

export const BRAND_COLORS: HSL[] = [
  { hue: 346, saturation: 80, lightness: 52 }, // red
  { hue: 20, saturation: 86, lightness: 55 }, // orange
  { hue: 44, saturation: 100, lightness: 63 }, // yellow
  { hue: 169, saturation: 58, lightness: 30 }, // green
  { hue: 208, saturation: 69, lightness: 51 }, // blue
  { hue: 257, saturation: 64, lightness: 58 }, // purple
  { hue: 336, saturation: 100, lightness: 68 }, // pink
  { hue: 0, saturation: 0, lightness: 53 }, // gray
  { hue: 35, saturation: 42, lightness: 54 }, // brown
  { hue: 0, saturation: 0, lightness: 100 }, // white
];

export const DEFAULT_PICKER_COLOR: HSL = {
  hue: 249,
  saturation: 100,
  lightness: 90,
  alpha: 1,
}; // hsla(249, 100%, 90%, 1.00)

/**
 * Print typography size constants
 */
export const PRINT_TYPOGRAPHY = {
  sizes: {
    xs: '.75rem', // 12px
    sm: '.875rem', // 14px
    md: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
  },
  lineHeights: {
    tight: 1.2,
    base: 1.75,
    relaxed: 2,
    loose: 2.25,
  },
  weights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

/**
 * Print spacing constants
 */
export const PRINT_SPACING = {
  page: {
    margin: '0.75in',
    gutter: '0.5in',
  },
  section: {
    margin: '2rem',
    padding: '1.5rem',
  },
  component: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  list: {
    indent: '1.5rem',
    bullet: '2rem',
    item: '1rem',
    border: '0.25rem',
  },
} as const;
